// Game Constants
export const GRAVITY = 0.5;
export const JUMP_FORCE = 12; // Positive = upward jump impulse (used with jumpZ system)
export const MOVE_SPEED = 5;
export const BULLET_SPEED = 12;
export const ENEMY_SPEED = 2.3;
export const GROUND_HEIGHT = 180; // Expanded for 2.5D depth movement

// 2.5D Depth Movement Constants
export const DEPTH_MOVE_SPEED = 3; // Speed for up/down ground movement
export const ROAD_TOP_MARGIN = 15; // Margin from road top to first walkable position
export const WALKABLE_DEPTH = 150; // Total walkable depth range in pixels
export const DEPTH_COLLISION_THRESHOLD = 25; // Depth proximity for entity-to-entity collision
export const BULLET_DEPTH_THRESHOLD = 40; // Wider threshold for bullet-to-entity collision
export const DEFAULT_GROUND_Y = 75; // Default spawn depth (middle of road)

// 2.5D Depth Scaling (perspective effect)
export const DEPTH_SCALE_MIN = 0.75; // Scale at back of road (groundY=0)
export const DEPTH_SCALE_MAX = 1.12; // Scale at front of road (groundY=WALKABLE_DEPTH)

// Helper: Get perspective scale factor from groundY
export const getDepthScale = (groundY) => {
  const t = Math.max(0, Math.min(1, groundY / WALKABLE_DEPTH));
  return DEPTH_SCALE_MIN + t * (DEPTH_SCALE_MAX - DEPTH_SCALE_MIN);
};

// Helper: Convert groundY to screen feet Y
export const getScreenFeetY = (canvasHeight, groundY) => {
  return canvasHeight - GROUND_HEIGHT + ROAD_TOP_MARGIN + groundY;
};

// Helper: Get entity draw Y from groundY, height, and jumpZ
export const getDrawY = (canvasHeight, groundY, entityHeight, jumpZ = 0) => {
  return getScreenFeetY(canvasHeight, groundY) - entityHeight - jumpZ;
};

// Background images for each level theme.
// All 20 levels use AI-generated pixel-art skies (Gemini Nano Banana) served
// directly from CRA public/backgrounds/. See backend/tools/generate_level_skies.py.
export const LEVEL_BACKGROUNDS = {
  1:  '/backgrounds/level_1.jpg',
  2:  '/backgrounds/level_2.jpg',
  3:  '/backgrounds/level_3.jpg',
  4:  '/backgrounds/level_4.jpg',
  5:  '/backgrounds/level_5.jpg',
  6:  '/backgrounds/level_6.jpg',
  7:  '/backgrounds/level_7.jpg',
  8:  '/backgrounds/level_8.jpg',
  9:  '/backgrounds/level_9.jpg',
  10: '/backgrounds/level_10.jpg',
  11: '/backgrounds/level_11.jpg',
  12: '/backgrounds/level_12.jpg',
  13: '/backgrounds/level_13.jpg',
  14: '/backgrounds/level_14.jpg',
  15: '/backgrounds/level_15.jpg',
  16: '/backgrounds/level_16.jpg',
  17: '/backgrounds/level_17.jpg',
  18: '/backgrounds/level_18.jpg',
  19: '/backgrounds/level_19.jpg',
  20: '/backgrounds/level_20.jpg',
};

// Background image cache
export const backgroundImageCache = {};

export const loadBackgroundImage = (level) => {
  const url = LEVEL_BACKGROUNDS[level] || LEVEL_BACKGROUNDS[1];
  if (!backgroundImageCache[url]) {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = url;
    backgroundImageCache[url] = { img, loaded: false };
    img.onload = () => {
      backgroundImageCache[url].loaded = true;
    };
    img.onerror = () => {
      console.warn(`Failed to load background for level ${level}`);
      backgroundImageCache[url].loaded = false;
    };
  }
  return backgroundImageCache[url];
};

/**
 * Pre-warm the browser cache for all 20 level backgrounds during main-menu
 * idle time, so hitting Play or advancing to level N never shows the CSS
 * gradient fallback flash. Uses requestIdleCallback (or setTimeout fallback)
 * to avoid competing with initial menu-render paint.
 *
 * Safe to call multiple times — `loadBackgroundImage` already dedupes by URL.
 * Returns a Promise that resolves once every image is either loaded or errored,
 * so callers can await the "ready" state (unused today but useful for splash).
 */
export const preloadAllBackgrounds = () => {
  const schedule = (fn) => {
    if (typeof window === 'undefined') { fn(); return; }
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(fn, { timeout: 2000 });
    } else {
      setTimeout(fn, 300);
    }
  };

  return new Promise((resolve) => {
    schedule(() => {
      const levels = Object.keys(LEVEL_BACKGROUNDS).map(Number).sort((a, b) => a - b);
      let remaining = levels.length;
      if (remaining === 0) { resolve(); return; }
      const done = () => { if (--remaining <= 0) resolve(); };
      for (const level of levels) {
        const entry = loadBackgroundImage(level);
        if (entry.loaded) { done(); continue; }
        // Attach one-shot listeners without disturbing loadBackgroundImage's
        // own onload (which writes .loaded = true). onload runs first in the
        // Image event queue, so entry.loaded is already true when we fire.
        const prevOnload = entry.img.onload;
        const prevOnerror = entry.img.onerror;
        entry.img.onload = (e) => { if (prevOnload) prevOnload(e); done(); };
        entry.img.onerror = (e) => { if (prevOnerror) prevOnerror(e); done(); };
      }
    });
  });
};

// Power-up types
export const POWER_UP_TYPES = {
  RAPID_FIRE: 'rapid_fire',
  SHIELD: 'shield',
  HEALTH: 'health',
  GRENADE: 'grenade',
  SPEED: 'speed',
  SPREAD_SHOT: 'spread_shot',
  DOUBLE_DAMAGE: 'double_damage',
  FREEZE: 'freeze',
  MAGNET: 'magnet',
  LASER: 'laser',
  EXPLOSIVE_ROUNDS: 'explosive_rounds',
  EXTRA_LIFE: 'extra_life'
};

// Weapon types
export const WEAPON_TYPES = {
  DEFAULT: 'default',
  ROCKET_LAUNCHER: 'rocket_launcher',
  MINI_TANK: 'mini_tank',
  ASSAULT_RIFLE: 'assault_rifle'
};
